import { KeyboardEvent, SelectHTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildClassStringFromClassMap } from '@steelbuy/util';

import { DropdownPanelPortal } from '../dropdown-panel-portal/DropdownPanelPortal';
import { Icon } from '../icon/Icon';
import { IconIdentifier } from '../icon/Icon.enums';
import { validate, ValidationProps } from '../input-validation/InputValidation';

import './InputSelect.scss';

const DEFAULT_TEST_ID = 'input-select';

export type InputSelectOption<T> = {
    label: string;
    value: T;
};

export type InputSelectProps<T> = {
    label?: string;
    placeholder?: string;
    options?: Array<InputSelectOption<T>>;
    isReadonly?: boolean;
    helperText?: string;
    error?: string;
    onSelect?: (value?: T) => void;
    testId?: string;
    id?: string;
    searchable?: boolean;
} & ValidationProps &
    Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onSelect' | 'onBlur' | 'onChange'>;

export const InputSelect = <T extends string>({
    disabled,
    error,
    forceValidation,
    helperText,
    isReadonly,
    label,
    onSelect,
    options = [],
    placeholder,
    testId,
    validators,
    value,
    defaultValue,
    id,
    searchable,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...rest
}: InputSelectProps<T>) => {
    const resolvedInitial = (value ?? defaultValue) as T | undefined;

    const { t } = useTranslation(['uiPrimitive']);

    const [isOpen, setIsOpen] = useState(false);
    const isOpenRef = useRef(false);
    const [internalValue, setInternalValue] = useState<T | undefined>(resolvedInitial);
    const [filterText, setFilterText] = useState('');
    const [isTouchedInternal, setIsTouchedInternal] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    // Only show the highlighted style when the user is actively using keyboard navigation
    const keyboardNavigating = useRef(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLSpanElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const filterInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        setInternalValue(value as T | undefined);
    }, [value]);

    const doValidate = useCallback((val: typeof internalValue) => validate(validators ?? [], val), [validators]);
    const validationError = forceValidation || isTouchedInternal ? doValidate(internalValue) : undefined;
    const combinedError = validationError ?? error;

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            const target = e.target as Node;
            const inPanel = panelRef.current?.contains(target);
            const onTrigger = triggerRef.current?.contains(target);
            // Stay open only if clicking the trigger (handled by onClick toggle) or inside panel
            if (!inPanel && !onTrigger) {
                isOpenRef.current = false;
                setIsOpen(false);
                setIsTouchedInternal(true);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
            return () => document.removeEventListener('mousedown', handleOutsideClick);
        }
        return undefined;
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setFilterText('');
            setHighlightedIndex(-1);
            keyboardNavigating.current = false;
        }
    }, [isOpen]);

    // Scroll the highlighted item into view within the dropdown <ul>.
    // When searchable, the filter input is position:sticky at the top, so we
    // subtract its height from the top boundary to avoid items scrolling under it.
    // We use offsetTop (relative to the <ul>) since both item and list share the
    // same offsetParent — the portal wrapper div with position:absolute.
    useEffect(() => {
        if (highlightedIndex < 0) return;
        const listId = `${id ?? 'default-input-select'}-listbox`;
        const itemId = `${id ?? 'default-input-select'}-opt-${highlightedIndex}`;
        const list = document.getElementById(listId);
        const item = document.getElementById(itemId);
        if (!item || !list) return;
        const filterEl = list.querySelector<HTMLElement>('.input-select__field__list__filter');
        const stickyHeight = filterEl ? filterEl.offsetHeight : 0;
        // offsetTop of the item relative to the list's scroll container
        const itemTop = item.offsetTop - list.offsetTop - stickyHeight;
        const itemBottom = item.offsetTop - list.offsetTop + item.offsetHeight;
        if (list.scrollTop > itemTop) {
            list.scrollTop = itemTop;
        } else if (list.scrollTop + list.clientHeight < itemBottom) {
            list.scrollTop = itemBottom - list.clientHeight;
        }
    }, [highlightedIndex]);

    const isInteractive = !disabled && !isReadonly;

    const selectedOption = options.find((o) => o.value === internalValue);
    const selectedLabel = selectedOption?.label;

    const filteredOptions =
        searchable && filterText.length > 0
            ? options.filter((o) => o.label.toLowerCase().includes(filterText.toLowerCase()))
            : options;

    const openDropdown = () => {
        if (!isInteractive) return;
        isOpenRef.current = true;
        setIsOpen(true);
        // Pre-highlight the current selection so arrow keys move relative to it
        const currentIndex = filteredOptions.findIndex((o) => o.value === internalValue);
        setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
    };

    const closeDropdown = (markTouched = true, returnFocus = true) => {
        isOpenRef.current = false;
        setIsOpen(false);
        if (markTouched) setIsTouchedInternal(true);
        if (returnFocus) triggerRef.current?.focus();
    };

    const handleToggle = () => {
        if (!isInteractive) return;
        if (isOpen) {
            closeDropdown();
        } else {
            openDropdown();
        }
    };

    const handleSelect = (option: InputSelectOption<T>) => {
        setInternalValue(option.value);
        closeDropdown(true);
        onSelect?.(option.value);
    };

    const handleClearPlaceholder = () => {
        setInternalValue(undefined);
        closeDropdown(true);
        onSelect?.(undefined);
    };

    const navigateOptions = (direction: 1 | -1) => {
        keyboardNavigating.current = true;
        if (!isOpen) {
            openDropdown();
            return;
        }
        setHighlightedIndex((prev) => {
            const max = filteredOptions.length - 1;
            if (max < 0) return -1;
            if (prev < 0) return direction === 1 ? 0 : max;
            return Math.max(0, Math.min(max, prev + direction));
        });
    };

    const confirmHighlighted = () => {
        if (!isOpen || highlightedIndex < 0) return;
        const option = filteredOptions[highlightedIndex];
        if (option) handleSelect(option);
    };

    const handleTriggerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                navigateOptions(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                navigateOptions(-1);
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen) {
                    confirmHighlighted();
                } else {
                    openDropdown();
                }
                break;
            case 'Tab':
                // Close without preventing Tab so focus moves naturally to the next element
                if (isOpen) closeDropdown(true, false);
                break;
            case 'Escape':
                closeDropdown(true);
                break;
            default:
                break;
        }
    };

    const focusNextFromTrigger = (reverse = false) => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const focusable = Array.from(
            document.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((el) => !el.closest('[aria-hidden="true"]'));
        const triggerIndex = focusable.indexOf(trigger);
        if (triggerIndex < 0) return;
        const nextIndex = reverse ? triggerIndex - 1 : triggerIndex + 1;
        if (nextIndex >= 0 && nextIndex < focusable.length) {
            focusable[nextIndex].focus();
        }
    };

    const handleFilterKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                navigateOptions(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                navigateOptions(-1);
                break;
            case 'Enter':
                e.preventDefault();
                confirmHighlighted();
                break;
            case 'Tab':
                e.preventDefault();
                closeDropdown(true, false);
                triggerRef.current?.focus();
                focusNextFromTrigger(e.shiftKey);
                break;
            case 'Escape':
                closeDropdown(true);
                break;
            default:
                break;
        }
    };

    // Reset highlight when filter text changes
    useEffect(() => {
        setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
    }, [filterText]);

    const rootClassMap = {
        'input-select': true,
        'input-select--disabled': !!disabled,
        'input-select--readonly': !!isReadonly,
    };

    const fieldClassMap = {
        'input-select__field': true,
        'input-select__field--error': !!combinedError,
        'input-select__field--open': isOpen,
    };

    const triggerClassMap = {
        'input-select__field__trigger': true,
        'input-select__field__trigger--disabled': !!disabled,
        'input-select__field__trigger--read-only': !!isReadonly,
        'input-select__field__trigger--placeholder': !selectedLabel,
    };

    const helperTextClassMap = {
        'input-select__helper-text': true,
        'input-select__helper-text--error': !!combinedError,
    };

    return (
        <div className={buildClassStringFromClassMap(rootClassMap)} ref={containerRef}>
            {label && (
                <span className="input-select__label">
                    {label}
                    {validators && <span className="required-field"> *</span>}
                </span>
            )}

            {/* Hidden input for test selectors */}
            <input
                type="text"
                aria-hidden="true"
                tabIndex={-1}
                readOnly
                value={internalValue ?? ''}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                id={id ?? 'default-input-select'}
                data-testid={testId || DEFAULT_TEST_ID}
            />

            <span className={buildClassStringFromClassMap(fieldClassMap)} ref={fieldRef}>
                <div
                    ref={triggerRef}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-controls={`${id ?? 'default-input-select'}-listbox`}
                    aria-activedescendant={
                        isOpen && highlightedIndex >= 0
                            ? `${id ?? 'default-input-select'}-opt-${highlightedIndex}`
                            : undefined
                    }
                    tabIndex={isInteractive ? 0 : -1}
                    className={buildClassStringFromClassMap(triggerClassMap)}
                    onClick={handleToggle}
                    onKeyDown={handleTriggerKeyDown}
                    onBlur={(e) => {
                        if (isOpenRef.current) return;
                        const focusingIntoPanel = panelRef.current?.contains(e.relatedTarget as Node);
                        if (!focusingIntoPanel) {
                            setIsTouchedInternal(true);
                        }
                    }}
                >
                    <span className="input-select__field__trigger__sizer" aria-hidden="true">
                        {placeholder && (
                            <span className="input-select__field__trigger__sizer__item">{placeholder}</span>
                        )}
                        {options.map((o) => (
                            <span key={o.value} className="input-select__field__trigger__sizer__item">
                                {o.label}
                            </span>
                        ))}
                    </span>
                    <span className="input-select__field__trigger__label">{selectedLabel ?? placeholder ?? ''}</span>
                </div>

                <span
                    className="input-select__field__icons"
                    style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}
                >
                    {combinedError && <Icon name={IconIdentifier.ERROR} />}
                    <Icon name={isOpen ? IconIdentifier.CHEVRON_UP : IconIdentifier.CHEVRON_DOWN} />
                </span>
            </span>

            <DropdownPanelPortal
                triggerRef={fieldRef}
                isOpen={isOpen}
                panelRef={panelRef}
                onMount={searchable ? () => filterInputRef.current?.focus() : undefined}
                className="input-select__panel"
                onScrollClose={() => closeDropdown(false)}
            >
                <ul
                    ref={listRef}
                    role="listbox"
                    id={`${id ?? 'default-input-select'}-listbox`}
                    className="input-select__field__list"
                    onMouseLeave={() => setHighlightedIndex(-1)}
                >
                    {searchable && (
                        <li className="input-select__field__list__filter">
                            <input
                                ref={filterInputRef}
                                type="text"
                                className="input-select__field__list__filter__input"
                                placeholder={t('uiPrimitive:dropdown.filterPlaceholder')}
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={handleFilterKeyDown}
                            />
                        </li>
                    )}

                    {placeholder && !filterText && (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                        <li
                            role="option"
                            aria-selected={!selectedLabel}
                            className="input-select__field__list__option input-select__field__list__option--placeholder"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleClearPlaceholder}
                        >
                            {placeholder}
                        </li>
                    )}

                    {filteredOptions.length === 0 ? (
                        <li className="input-select__field__list__no-options">{t('uiPrimitive:dropdown.noOptions')}</li>
                    ) : (
                        filteredOptions.map((option, index) => (
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                            <li
                                key={option.value}
                                id={`${id ?? 'default-input-select'}-opt-${index}`}
                                role="option"
                                aria-selected={option.value === internalValue}
                                className={buildClassStringFromClassMap({
                                    'input-select__field__list__option': true,
                                    'input-select__field__list__option--selected': option.value === internalValue,
                                    'input-select__field__list__option--highlighted':
                                        index === highlightedIndex && keyboardNavigating.current,
                                })}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelect(option)}
                                onMouseEnter={() => {
                                    keyboardNavigating.current = false;
                                    setHighlightedIndex(index);
                                }}
                            >
                                {option.label}
                                {option.value === internalValue && <Icon name={IconIdentifier.OK} />}
                            </li>
                        ))
                    )}
                </ul>
            </DropdownPanelPortal>

            {(helperText ?? combinedError) && (
                <span className={buildClassStringFromClassMap(helperTextClassMap)}>{helperText ?? combinedError}</span>
            )}
        </div>
    );
};
